import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
} from "react";
import Intercom from "@intercom/messenger-js-sdk";
import { useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { safeLocalStorage } from "../utils/safeStorage";

const APP_ID = "i66hiqpj";

const isIntercomEnabled = (): boolean =>
  process.env.REACT_APP_INTERCOM_ENABLED !== "false";

/** Messenger Security requires a valid JWT — skip boot when absent (demo / local). */
const readIntercomToken = (): string | undefined => {
  const token = safeLocalStorage.getItem("intercomToken");
  return token && token !== "undefined" ? token : undefined;
};

type IntercomContextValue = {
  /** Fire a custom event visible in Intercom's event stream. */
  trackEvent: (name: string, metadata?: Record<string, unknown>) => void;
  /** Programmatically open the Intercom messenger. */
  showMessenger: () => void;
  /** Live chat can open: signed in, not switched off, app id and user token present. */
  available: boolean;
};

const IntercomContext = createContext<IntercomContextValue>({
  trackEvent: () => {},
  showMessenger: () => {},
  available: false,
});

export const useIntercom = () => useContext(IntercomContext);

export const IntercomProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isLoggedIn } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!isLoggedIn || !isIntercomEnabled() || !readIntercomToken()) return;
    try {
      window.Intercom?.("update");
    } catch {
      // Non-blocking — Intercom is optional for app functionality.
    }
  }, [location.pathname, isLoggedIn]);

  useEffect(() => {
    if (!isIntercomEnabled()) return;

    if (isLoggedIn) {
      const intercomToken = readIntercomToken();
      if (!intercomToken) return;

      const email = safeLocalStorage.getItem("userEmail") ?? undefined;
      const userId = safeLocalStorage.getItem("userId") ?? undefined;
      const name = safeLocalStorage.getItem("userName") ?? undefined;
      try {
        Intercom({
          app_id: APP_ID,
          // The floating bubble never shows; Help & support -> Live chat opens the messenger.
          hide_default_launcher: true,
          intercom_user_jwt: intercomToken,
          ...(userId ? { user_id: userId } : {}),
          ...(name ? { name } : {}),
          ...(email ? { email } : {}),
        });
      } catch {
        // Non-blocking — demo envs may lack Intercom identity verification.
      }
    } else {
      try {
        window.Intercom?.("shutdown");
      } catch {
        // Ignore shutdown failures.
      }
    }
  }, [isLoggedIn]);

  const trackEvent = useCallback(
    (name: string, metadata?: Record<string, unknown>) => {
      window.Intercom?.("trackEvent", name, metadata);
    },
    [],
  );

  const showMessenger = useCallback(() => {
    window.Intercom?.("show");
  }, []);

  const available = Boolean(
    isLoggedIn && isIntercomEnabled() && APP_ID && readIntercomToken(),
  );

  return (
    <IntercomContext.Provider value={{ trackEvent, showMessenger, available }}>
      {children}
    </IntercomContext.Provider>
  );
};
